import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { Searches, Filters, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import { Symbol } from "../../../../../../../models/symbol";
import SymbolsService from "../../../../../../../services/symbols.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { SymbolType } from "../../../../../../../util/enums";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";

const useActions = () => {
  const { currentUser } = useAppState();

  const [symbols, setSymbols] = useState<Symbol[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["symbols.list", `${SymbolType.LOCATION}`, page, pageSize, filters, searches, sorting],
    () =>
      SymbolsService.getAll({
        page,
        pageSize,
        type: `${SymbolType.LOCATION}`,
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["name", "asc"]])
          : JSON.stringify([["name", "asc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setSymbols(response.data);
      },
    }
  );

  const { refetch: refetchSymbolsTotal } = useQuery(
    ["symbols.total", `${SymbolType.LOCATION}`, filters, searches],
    () =>
      SymbolsService.count({
        companyId: currentUser?.companyId,
        type: `${SymbolType.LOCATION}`,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data !== null) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateSymbol, isLoading: isUpdating } = useMutation(
    ["symbols.update"],
    ({ id, symbol }: { id: number; symbol: Symbol }) => SymbolsService.update(id, symbol),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchSymbolsTotal();
      },
      onError: (error: any) => {
        setHasErrors(true);
        NotificationToast.error(error.response.data);
      },
    }
  );

  const { mutate: createSymbol, isLoading: isCreating } = useMutation(
    ["symbols.create"],
    (symbol: Symbol) => SymbolsService.create(symbol),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchSymbolsTotal();
      },
      onError: (error: any) => {
        setHasErrors(true);
        NotificationToast.error(error.response.data);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    symbols,
    page,
    pageSize,
    total,
    hasErrors,
    isLoading: isFetching || isCreating || isUpdating,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createSymbol,
    updateSymbol,
  };
};

export default useActions;
